<script lang="ts">
	import type { AdFormats } from '$lib/services/advertising';
	import { getAdSizeFromDevice } from '$lib/components/advertising/utils';
	import { onMount } from 'svelte';

	interface Props {
		adFormat: AdFormats;
		adId: string;
		// By default, hide the ad until we know it's ready to be shown
		showAdByDefault?: boolean;
		adIdForTests: string;
		onAdAvailabilityChange?: (isAvailable: boolean) => void;
	}

	const {
		adFormat,
		adId,
		showAdByDefault = false,
		adIdForTests,
		onAdAvailabilityChange,
	}: Props = $props();

	let isAvailable = $state(false);

	function getAdSize(size: 'mobile' | 'desktop') {
		return getAdSizeFromDevice(adFormat, size);
	}

	onMount(() => {
		function handleRenderEnded(event: googletag.events.SlotRenderEndedEvent) {
			const slot = event.slot;
			const id = slot.getSlotElementId();

			if (id === adId) {
				isAvailable = event.isEmpty === false;
			}

			onAdAvailabilityChange?.(isAvailable);
		}

		window.googletag?.cmd.push(() => {
			// All ads use google tag publisher under the hood so this event listener
			// can be used for all ad providers
			googletag.pubads().addEventListener('slotRenderEnded', handleRenderEnded);
		});

		return () => {
			try {
				window.googletag?.pubads?.().removeEventListener('slotRenderEnded', handleRenderEnded);
			} catch (e) {
				console.error('Error removing ad event listener', e);
			}
		};
	});

	const showAd = $derived(isAvailable || showAdByDefault);
</script>

<!-- add overflow hidden to prevent the ad from overflowing its container in the case the ad is larger than what was requested -->
<div
	id={adId}
	class={[
		'ad size-full max-h-[var(--height)] max-w-[var(--width)] overflow-hidden md:max-h-[var(--md-height)] md:max-w-[var(--md-width)]',
		!showAd && 'hidden',
	]}
	style="
		--width:{getAdSize('mobile')[0]}px;
			--height:{getAdSize('mobile')[1]}px;
			--md-width:{getAdSize('desktop')[0]}px;
			--md-height:{getAdSize('desktop')[1]}px;
	"
	data-testid={`ad-${adIdForTests}`}
></div>

<style>
	/*	Force the height and width of the ad iframes
	Our sanitization library strips out the height and width attributes */
	.ad :global(iframe) {
		height: 100%;
		width: 100%;
	}
</style>
